import { FlexContainer } from 'features/common/components/FlexContainer';
import { Modal } from 'features/common/components/Modal/Modal';
import {
	ContainerActionButtons,
	ContainerBody,
	FieldLabel,
	ModalButton,
	TextInput,
	TicketTypeSelect,
} from './LotteryCodesModal.styles';
import { useState } from 'react';
import { PRIMARY, QUATERNARY } from 'features/common/components/Button/constants';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { api } from 'services/api';
import { downloadXLSFile } from 'utils/utils';
import { TicketType } from 'features/common/types';

interface LotteryCodesModalProps {
	closeModal: () => void;
	eventId: string;
	ticketTypes: TicketType[];
}

export const LotteryCodesModal = ({ closeModal, eventId, ticketTypes }: LotteryCodesModalProps) => {
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(false);
	const [numberOfCodes, setNumberOfCodes] = useState(100);
	const [ticketTypeId, setTicketTypeId] = useState<string>('');

	const handleDownloadCodesCsv = async () => {
		if (!ticketTypeId) return;
		setIsLoading(true);

		try {
			const { data } = await api.post(
				`/events/${eventId}/lottery/codes`,
				{ ticket_type_id: Number(ticketTypeId), quantity: numberOfCodes },
				{ responseType: 'blob' }
			);

			downloadXLSFile(data, 'Lottery codes');
		} catch (error) {
			toast.error(t('dashboard-lotteryCodes-error'));
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Modal onClose={closeModal}>
			<Modal.Header>{t('dashboard-lotteryCodes-title')}</Modal.Header>
			<Modal.Body>
				<ContainerBody>
					<FlexContainer flexDirection="column" gap="10px" width="100%">
						<FieldLabel htmlFor="lottery-codes-ticket-type">{t('dashboard-lotteryCodes-ticketType')}</FieldLabel>
						<TicketTypeSelect
							id="lottery-codes-ticket-type"
							value={ticketTypeId}
							onChange={(e) => setTicketTypeId(e.target.value)}
						>
							<option value="" disabled>
								{t('dashboard-lotteryCodes-selectTicketType')}
							</option>
							{ticketTypes.map((tt) => (
								<option key={tt.ID} value={tt.ID}>
									{tt.name}
								</option>
							))}
						</TicketTypeSelect>
					</FlexContainer>
					<FlexContainer flexDirection="column" gap="10px" width="100%">
						<FieldLabel htmlFor="lottery-codes-quantity">{t('dashboard-lotteryCodes-numberOfCodes')}</FieldLabel>
						<TextInput
							id="lottery-codes-quantity"
							type="number"
							value={numberOfCodes}
							onChange={(e) => setNumberOfCodes(Number(e.target.value))}
							min={1}
						/>
					</FlexContainer>
				</ContainerBody>
			</Modal.Body>

			<Modal.ActionButtons>
				<ContainerActionButtons>
					<ModalButton
						category={QUATERNARY}
						onClick={closeModal}
						text={t('button-cancel')}
						type="button"
						disabled={isLoading}
					/>
					<ModalButton
						category={PRIMARY}
						onClick={handleDownloadCodesCsv}
						text={isLoading ? t('dashboard-lotteryCodes-generating') : t('dashboard-lotteryCodes-generate')}
						type="button"
						disabled={isLoading || !ticketTypeId}
						isLoading={isLoading}
					/>
				</ContainerActionButtons>
			</Modal.ActionButtons>
		</Modal>
	);
};
